import {
  useState, useActionState, useRef, useEffect,
  ReactNode, Dispatch, SetStateAction, RefObject,
} from 'react';
import { useBjForm } from '@hooks/useBjForm';
import { useExtendedSearchParams } from '@hooks/next-routing-wrappers';
import type { RecordInterface } from '@type/form-types';
import { buildSearchParams } from '@utils/data-fetching/searchParams';
import type { AuthFormState, LoginFormValues } from '@auth/types';
import type { InputPasswordType } from '@auth/forms/formHelpers';
import { AuthRedirectQueryParams } from '@auth/constants';
import { ProviderType } from '@auth/types';

/** Types */
// Type for the action function that will be called on form submission or button click.
type AuthFormAction<TFormState> = (locale: Locale, redirectTo: string | null, prevState: TFormState, formData: FormData)
=> Promise<TFormState>;
type OAuthFormAction<TFormState> = (locale: Locale, redirectTo: string | null, provider: ProviderType, prevState: TFormState, formData: FormData)
=> Promise<TFormState>;
type MagicLinkFormAction<TFormState> = (locale: Locale, redirectTo: string | null, userToken: string | null, prevState: TFormState, formData: FormData)
=> Promise<TFormState>;

// Common props shared by both form types
interface BaseAuthFormProps<TFormState extends AuthFormState, TFormValues = LoginFormValues> {
  locale: Locale;
  className?: string;
  formAction: AuthFormAction<TFormState> | OAuthFormAction<TFormState> | MagicLinkFormAction<TFormState>;
  confirmationToken?: string | null;
  userToken?: string | null;
  withClientValidation?: boolean;
  autoSubmit?: boolean;
  /** Optional OAuth provider for social login forms. */
  oAuthProvider?: string;
  children?: (props: {
    /** The record function from useBjForm library. */
    record: RecordInterface;
    /** The current state of the form, including success/error messages. */
    formState: TFormState;
    /** A boolean indicating if the form submission is in progress. */
    isPending: boolean;
    /** The type of the password input, used to toggle visibility. */
    passwordInputType: InputPasswordType;
    /** A function to set the password input type, allowing toggling between 'password' and 'text'. */
    setPasswordInputType: Dispatch<SetStateAction<InputPasswordType>>;
    /** A reference to store form values in case of validation errors, allowing pre-filling of inputs after submission errors. */
    formValuesRef: RefObject<TFormValues | null>;
  }) => ReactNode;
}

/**
 * @description Base authentication form component that handles common functionality.
 * ** Props: **
 * - `locale`: The locale for translations.
 * - `className`: Optional class name to pass to the component. It will override the default class name.
 * - `formAction`: The action to be called on form submission, typically a login or register action (loginAction or registerAction).
 * - `confirmationToken`: Optional confirmation token for password reset actions.
 * - `userToken`: Optional user token for magic link actions.
 * - `withClientValidation`: Optional boolean to enable client-side form validation before submission. Defaults to true.
 * - `autoSubmit`: Optional boolean to enable automatic form submission on mount. Defaults to false.
 *
 * **Render Props (children):**
 * - `record`: The record function from useBjForm library.
 * - `formState`: The current state of the form, including success/error messages.
 * - `isPending`: A boolean indicating if the form submission is in progress.
 * - `passwordInputType`: The type of the password input (e.g., 'password' or 'text'). Used to toggle password visibility.
 * - `setPasswordInputType`: A function to set the password input type, allowing toggling between 'password' and 'text'.
 * - `formValuesRef`: A reference to store form values in case of validation errors, allowing pre-filling of inputs after submission errors.
 *
 * @example
 * <BaseAuthForm
 *   locale={locale}
 *   className={className}
 *   formAction={loginAction}
 * >
 *   {({ record, formState, isPending, passwordInputType, setPasswordInputType, formValuesRef }) => (
 *     <>
 *       <div className="relative text-left">
 *         <Input
 *           required
 *           type="text"
 *           className="asOptional"
 *           placeholder={t('global.label.email_or_phone')}
 *           pattern={`^${EMAIL_OR_PHONE_PATTERN}$`}
 *           patternMismatchMessage={t('auth.email.invalid-format.message')}
 *           {...record('email', {
 *             defaultValue: formValuesRef?.current?.email || '',
 *           })}
 *         />
 *       </div>
 *
 *       <div className="relative text-left">
 *         <Input
 *           required
 *           type={inputType}
 *           className="asOptional"
 *           placeholder={t('global.label.password')}
 *           minLength={6}
 *           {...record('password', {
 *             defaultValue: formValuesRef?.current?.password || '',
 *           })}
 *         />
 *
 *         <div className="absolute right-1.5 top-2 mt-px">
 *           <Button
 *             type="button"
 *             styling="none"
 *             className="p-1.5"
 *             onClick={() => togglePasswordVisibility(setPasswordInputType)}
 *           >
 *             {passwordVisibilityIcons[passwordInputType]}
 *           </Button>
 *         </div>
 *       </div>
 *
 *       <Label className="relative !mt-0 flex cursor-pointer items-center justify-center rounded bg-secondary/10 py-2">
 *         <Input
 *           type="checkbox"
 *           {...record('walletType', {
 *             defaultValue: 'business',
 *             defaultChecked: formValuesRef?.current?.walletType === 'business',
 *           })}
 *         />
 *         <RadioText className="mt-px text-sm leading-none">
 *           {t('register.label.hire')}
 *         </RadioText>
 *       </Label>
 *
 *       {formState?.errorText && (
 *         <div className="mb-2 rounded bg-error/10 p-2 text-sm text-error">
 *           {formState.errorText}
 *         </div>
 *       )}
 *
 *       <Button
 *         type="submit"
 *         color="secondary"
 *         rounding="full"
 *         className="mt-2 w-full !font-bold"
 *         isLoading={isPending}
 *       >
 *         {t('sign_in.button')}
 *       </Button>
 *     </>
 *   )}
 * </BaseAuthForm>
 */
export const BaseAuthForm = <TFormState extends AuthFormState, TFormValues = LoginFormValues>(props: BaseAuthFormProps<TFormState, TFormValues>) => {
  // Destructure props.
  const {
    locale,
    className = '',
    formAction,
    confirmationToken,
    userToken,
    withClientValidation = true,
    oAuthProvider,
    autoSubmit = false,
    children,
  } = props;

  // Get the search params from the URL for redirects or other purposes.
  const { searchParamsAsObject } = useExtendedSearchParams();

  // Used to toggle the password visibility in the form.
  const [passwordInputType, setPasswordInputType] = useState<InputPasswordType>('password');

  // Used to store the form values in case of validation errors.
  // Use this to prefill the form inputs because forms are reset on submitting when using actions.
  const formValuesRef = useRef<TFormValues | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  // Form hook functions
  const {
    record,
    checkFormValidation,
    handleActionErrors,
    submitTrigger,
  } = useBjForm();


  // Extract the redirect path from the search params. These should always be above any normal redirects.
  // UrlSearchParams used for redirect are: `_target_path` (symfony, old) and `targetPath` (react, new).
  // *************************
  let urlRedirectTo: string | null = null;

  const {
    [AuthRedirectQueryParams.symfony]: symfonyRedirect,
    [AuthRedirectQueryParams.react]: reactRedirect,
    ...restSearchParams
  } = searchParamsAsObject;

  const paramsForRedirect = buildSearchParams(restSearchParams);

  if (typeof symfonyRedirect === 'string') {
    urlRedirectTo = `${decodeURIComponent(symfonyRedirect)}${paramsForRedirect}`;
  } else if (typeof reactRedirect === 'string') {
    urlRedirectTo = `${decodeURIComponent(reactRedirect)}${paramsForRedirect}`;
  }


  // Form action management
  // *************************
  // Bind the locale and redirect to the action.
  let submitFormAction: (prevState: TFormState, formData: FormData) => Promise<TFormState>;
  switch (true) {
    // For OAuth providers (google, facebook, etc.) action
    case !!oAuthProvider:
      submitFormAction = (formAction as OAuthFormAction<TFormState>).bind(null, locale, urlRedirectTo, oAuthProvider as ProviderType);
      break;
    // For magic link action
    case !!userToken:
      submitFormAction = (formAction as MagicLinkFormAction<TFormState>).bind(null, locale, urlRedirectTo, userToken);
      break;
    // For regular login, register, reset actions
    default:
      submitFormAction = (formAction as AuthFormAction<TFormState>).bind(null, locale, confirmationToken || urlRedirectTo);
      break;
  }

  // Handle form submission with validation and error handling.
  const handleSubmit = async (prevState: TFormState, formData: FormData) => {
    // Store the form values in the ref for validation and error handling.
    formValuesRef.current = Object.fromEntries(formData.entries()) as TFormValues;
    // Do some form validation before submitting
    if (formRef?.current && withClientValidation) {
      const isValid = !!checkFormValidation(formRef, formData);
      if (!isValid) return prevState;
    }
    // Start the transition for the form submission if the form is valid.
    const result = await submitFormAction(prevState, formData);
    // Handle API errors (not AuthJs errors)
    if (result.errorData) {
      handleActionErrors(result.errorData);
    }
    // If the server action returned a redirect URL, perform a hard navigation.
    // This ensures a full page load so the root layout re-renders with a fresh session.
    if (result.success && result.redirectTo) {
      window.location.href = result.redirectTo;
      return result;
    }
    // All good
    return result;
  };

  // Manage the form state and actions
  const [formState, actionFn, isPending] = useActionState(handleSubmit, {
    success: false,
    errorText: null,
  } as Awaited<TFormState>);

  // If autoSubmit is enabled, trigger the form submission on mount.
  // Wait for the next tick to ensure the form is fully mounted and ready.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (autoSubmit) submitTrigger(formRef);
    }, 0);
    return () => clearTimeout(timer);
  }, [submitTrigger, autoSubmit]);


  // Render content
  // *************************
  return (
    <form
      ref={formRef}
      action={actionFn}
      noValidate
      className={className || 'relative grid grid-cols-1 gap-2'}
    >
      {/* Render children with the necessary props */}
      {children && children({
        record,
        formState,
        isPending,
        passwordInputType,
        setPasswordInputType,
        formValuesRef,
      })}
    </form>
  );
};
